const VALID_GTIN_LENGTHS = new Set([8, 12, 13, 14])

/** Remove tudo que não é dígito — o pipeline compara digits-only. */
export const normalizeGtin = (value: string) => value.replaceAll(/\D/g, '')

/**
 * Valida GTIN pelo dígito verificador mod-10 (GS1: 8, 12, 13 ou 14 dígitos).
 * Mesmo algoritmo da query do pipeline e do validator da API.
 */
export const isValidGtin = (value: string) => {
  const gtin = normalizeGtin(value)

  if (!VALID_GTIN_LENGTHS.has(gtin.length)) return false

  const digits = gtin.split('').map(Number)
  const checkDigit = digits.pop()

  digits.reverse()

  const sum = digits.reduce(
    (acc, digit, index) => acc + digit * (index % 2 === 0 ? 3 : 1),
    0
  )

  return (10 - (sum % 10)) % 10 === checkDigit
}

/** Célula que só tem dígitos e separadores é código; nome e cabeçalho não são. */
const looksLikeCode = (cell: string) => /^[\d\s.-]+$/.test(cell)

/**
 * Quebra uma colagem em lote nos GTINs válidos e únicos. Cada linha aceita
 * vários códigos (`7891…, 7899…`); célula que não é código (cabeçalho, nome
 * de marca) e código com dígito verificador errado são ignorados.
 */
export const parseGtins = (raw: string) => {
  const seen = new Set<string>()

  raw.split(/\r?\n/).forEach(line => {
    line
      .split(/[,;\t]/)
      .map(cell => cell.trim())
      .filter(cell => cell && looksLikeCode(cell))
      .forEach(code => {
        const gtin = normalizeGtin(code)

        if (gtin && isValidGtin(gtin)) seen.add(gtin)
      })
  })

  return [...seen]
}

/** Célula de código: sem espaço e com ao menos um dígito (EAN ou SKU). */
const isProductCode = (cell: string) => !/\s/.test(cell) && /\d/.test(cell)

/** Só a célula com formato de EAN (dígitos e separadores) é normalizada; SKU com letras segue como veio. */
const isEanCell = (cell: string) => looksLikeCode(cell) && isValidGtin(cell)

/** Forma do código que a busca devolve: EAN só com dígitos, SKU como foi colado. */
const toCode = (cell: string) => (isEanCell(cell) ? normalizeGtin(cell) : cell)

/**
 * Quebra a colagem do lote em códigos, cada um buscado como EAN, como SKU ou
 * nos dois: EAN é a célula com dígito verificador válido; SKU é a célula sem
 * espaço e com ao menos um dígito. Cabeçalho e nome de marca (sem dígito) e
 * nome com espaço não são código. Um EAN válido também vale como SKU, porque
 * o PAC não adivinha o tipo pelo formato (um SKU de 8 dígitos pode passar no
 * dígito verificador). `codes` é cada código uma vez, na forma que a busca
 * devolve: EAN só com dígitos, SKU como foi colado. `skuOnly` são os códigos
 * que não têm formato de EAN. Numa linha com código, a célula que não é código
 * é a marca desses códigos (`brands`, pelo código).
 */
export const parseProductCodes = (raw: string, knownBrands: string[] = []) => {
  const gtins = parseGtins(raw)
  // a marca lida da coluna de um CSV nunca é código, qualquer que seja o formato
  const brandNames = new Set(knownBrands)
  const isCode = (cell: string) =>
    (isProductCode(cell) || isEanCell(cell)) && !brandNames.has(cell)
  const skus = new Set<string>()
  const skuOnly = new Set<string>()
  const codes = new Set<string>(gtins)
  // o texto da marca é livre e vira chave: mapa sem protótipo
  const brands: Record<string, string> = Object.create(null)

  // a marca do arquivo pode ter vírgula: ela sai da linha antes de a linha ser dividida
  const splitBrands = knownBrands
    .filter(brand => /[,;\t]/.test(brand))
    .sort((one, other) => other.length - one.length)

  raw.split(/\r?\n/).forEach(rawLine => {
    const fileBrand = splitBrands.find(brand => rawLine.includes(brand))
    const line = fileBrand ? rawLine.replace(fileBrand, '') : rawLine
    const cells = line
      .split(/[,;\t]/)
      .map(cell => cell.trim())
      .filter(Boolean)
    const brand = fileBrand ?? cells.find(cell => !isCode(cell))

    cells.filter(isCode).forEach(cell => {
      const code = toCode(cell)

      // célula com espaço só entra como EAN: fora do pedido de SKU
      if (isProductCode(cell)) skus.add(cell)

      codes.add(code)

      if (!isEanCell(cell)) skuOnly.add(cell)

      if (brand) {
        brands[code] = brand
        brands[cell] = brand
      }
    })
  })

  return {
    gtins,
    skus: [...skus],
    skuOnly: [...skuOnly],
    codes: [...codes],
    brands
  }
}

/** CSV em linhas de células: aspas, aspas duplas escapadas e quebra de linha dentro do campo. */
const parseCsvRows = (text: string) => {
  // o separador é o do cabeçalho; os outros caracteres são texto nas demais linhas
  const header = text.split(/\r?\n/)[0] ?? ''
  const delimiter = [',', ';', '\t'].reduce(
    (best, candidate) =>
      header.split(candidate).length > header.split(best).length
        ? candidate
        : best,
    ','
  )
  const rows: string[][] = []
  let row: string[] = []
  let cell = ''
  let quoted = false

  // aspas duplas e `\r\n` ocupam dois caracteres: o segundo é pulado
  let skipNext = false

  // por unidade de código, como o `text[index + 1]` abaixo
  text.split('').forEach((char, index) => {
    if (skipNext) {
      skipNext = false

      return
    }

    if (quoted) {
      if (char === '"' && text[index + 1] === '"') {
        cell += '"'
        skipNext = true
      } else if (char === '"') quoted = false
      else cell += char
    } else if (char === '"') quoted = true
    else if (char === delimiter) {
      row.push(cell.trim())
      cell = ''
    } else if (char === '\n' || char === '\r') {
      skipNext = char === '\r' && text[index + 1] === '\n'

      row.push(cell.trim())
      rows.push(row)
      row = []
      cell = ''
    } else cell += char
  })

  row.push(cell.trim())
  rows.push(row)

  return rows.filter(cells => cells.some(Boolean))
}

/**
 * CSV com cabeçalho `EAN`, `SKU` e `Marca` (a marca é opcional) vira as linhas
 * de código da caixa de lote, `código,marca` por linha (a marca é opcional): o
 * cabeçalho sai e, por linha, vale o EAN e só na falta dele o SKU. A marca vai
 * também à parte (`brands`, pelo código), porque a coluna é quem diz que o
 * valor é marca, qualquer que seja o formato dele (`3M`). Sem cabeçalho de EAN
 * ou SKU, o texto segue como veio.
 */
export const readCodesCsv = (text: string, withBrands = true) => {
  const rows = parseCsvRows(text.replace(/^\uFEFF/, ''))
  const header = rows[0] ?? []
  const columnOf = (pattern: RegExp) =>
    header.findIndex(cell => pattern.test(cell))

  const eanColumn = columnOf(/^(ean|gtin|c[oó]digo de barras)$/i)
  const skuColumn = columnOf(/^sku$/i)
  const brandColumn = withBrands ? columnOf(/^(marca|brand)$/i) : -1
  const brands: Record<string, string> = Object.create(null)

  if (eanColumn === -1 && skuColumn === -1) return { text, brands }

  const codes = rows.slice(1).flatMap(cells => {
    const code = cells[eanColumn] || cells[skuColumn]
    const brand = brandColumn === -1 ? '' : cells[brandColumn]

    if (!code) return []

    if (brand) {
      brands[toCode(code)] = brand
      brands[code] = brand
    }

    return [brand ? `${code},${brand}` : code]
  })

  return { text: codes.join('\n'), brands }
}

export type ParsedProductCodes = ReturnType<typeof parseProductCodes>

/**
 * Um produto do NTB é um EAN (vale em todos os publishers) ou um SKU com o
 * publisher onde ele vale. O mesmo código de SKU em outro publisher é outro
 * produto, por isso a chave do SKU leva o publisher.
 */
type Identity = {
  gtin: string | null
  sku: string | null
  publisherId: string | null
}

export const identityKey = ({ gtin, sku, publisherId }: Identity) =>
  gtin ?? `${publisherId}:${sku}`

/** Chave de uma linha de "Meus produtos". */
export const productKey = (product: AdvertiserBrandProduct) =>
  identityKey({
    gtin: product.gtin,
    sku: product.sku,
    publisherId: product.publisher_id
  })

/** Código exibido ao lado da etiqueta `EAN:` ou `SKU:`. */
export const productCode = (product: AdvertiserBrandProduct) =>
  product.gtin ?? product.sku ?? ''

/** Corpo de mover e remover: EANs em `gtins`, SKUs em `skus`. */
export const toIdentities = (
  products: AdvertiserBrandProduct[]
): AdvertiserBrandIdentities => {
  const gtins = products.flatMap(product =>
    product.gtin ? [product.gtin] : []
  )
  const skus = products.flatMap(product =>
    !product.gtin && product.sku && product.publisher_id
      ? [{ sku: product.sku, publisher_id: product.publisher_id }]
      : []
  )

  return {
    ...(gtins.length ? { gtins } : {}),
    ...(skus.length ? { skus } : {})
  }
}
